// Who gets which notification by email (D472). Every notification belongs to one category. In the app it is always
// shown to the people it concerns; by email it goes only to those whose choice for that category is on. Admins
// marked as technical contacts always get system alerts by email; if no admin is marked, every admin is treated as
// one, so a system alert is never sent to nobody. Each person can also ask for one email a day instead of one per
// event. Pure logic, shared by the app Worker, the pipeline Worker and the settings dialog.

import type { Role } from './permissions';

export const NOTIFY_CATEGORIES = ['system', 'review', 'access', 'reports', 'monitors'] as const;
export type NotifyCategory = (typeof NOTIFY_CATEGORIES)[number];
export const isNotifyCategory = (v: unknown): v is NotifyCategory => NOTIFY_CATEGORIES.includes(v as NotifyCategory);

export const NOTIFY_CATEGORY_LABELS: Record<NotifyCategory, { label: string; help: string; adminOnly: boolean }> = {
  system: {
    label: 'System alerts',
    help: 'Hoopoe itself needs care: a part of the system has stopped answering for 15 minutes, a processing job is stuck, the monthly AI budget is running out, and the daily health summary.',
    adminOnly: true,
  },
  review: {
    label: 'Review and match questions',
    help: 'Your uploads are ready for review, or need an answer from you before they can be processed.',
    adminOnly: false,
  },
  access: {
    label: 'New users and access requests',
    help: 'Someone asked for access to Hoopoe, or someone exported an unusual amount of data.',
    adminOnly: true,
  },
  reports: {
    label: 'Reports finished',
    help: 'Your scheduled reports ran, found new matches or could not run (for schedules set to email you).',
    adminOnly: false,
  },
  monitors: {
    label: 'Monitors and registers',
    help: 'Website monitors and social feeds you own found something new, or were switched off after failing again and again.',
    adminOnly: false,
  },
};

/**
 * The stored choices (`user_prefs.notification_settings`): `email:<category>`, `app:<category>` and `digest`. Missing
 * keys use the defaults.
 */
export type NotificationSettings = Record<string, boolean>;
export const emailKey = (c: NotifyCategory) => `email:${c}`;
export const appKey = (c: NotifyCategory) => `app:${c}`;
export const DIGEST_KEY = 'digest';

export interface RecipientContext {
  role: Role;
  /**
   * A technical contact in effect: an admin marked "Receives system alerts"; while none is marked, any admin who hasn't
   * turned system alert emails off (the fallback).
   */
  technicalContact: boolean;
  /**
   * System alert emails can't be turned off: a marked technical contact, or every admin when none is marked and all
   * of them turned them off (an alert never goes to nobody).
   */
  systemLocked: boolean;
}

/** Whether the category concerns this person at all (shown in their settings, delivered in the app). */
export const categoryApplies = (c: NotifyCategory, role: Role) => !NOTIFY_CATEGORY_LABELS[c].adminOnly || role === 'admin';

/**
 * Defaults per role: technical contacts get system alerts by email, other admins don't; admins keep the access
 * emails and everyone keeps the scheduled-report emails they get today. Review and monitor news was in-app only.
 */
export function emailDefault(c: NotifyCategory, ctx: Pick<RecipientContext, 'role' | 'technicalContact'>): boolean {
  switch (c) {
    case 'system':
      return ctx.technicalContact;
    case 'access':
      return ctx.role === 'admin';
    case 'reports':
      return true;
    case 'review':
    case 'monitors':
      return false;
  }
}

/** A choice the person can't change: see `systemLocked`. */
export const emailLocked = (c: NotifyCategory, ctx: RecipientContext) => c === 'system' && ctx.role === 'admin' && ctx.systemLocked;

export function emailOn(c: NotifyCategory, settings: NotificationSettings | null | undefined, ctx: RecipientContext): boolean {
  if (!categoryApplies(c, ctx.role)) return false;
  if (emailLocked(c, ctx)) return true;
  const v = settings?.[emailKey(c)];
  return typeof v === 'boolean' ? v : emailDefault(c, ctx);
}

/**
 * In the app: on unless the person turned it off for the category. System alerts by default reach the app only for
 * the people who get them by email (the others see the admin banner).
 */
export function inAppOn(c: NotifyCategory, settings: NotificationSettings | null | undefined, ctx: RecipientContext): boolean {
  if (!categoryApplies(c, ctx.role)) return false;
  const v = settings?.[appKey(c)];
  if (typeof v === 'boolean') return v;
  return c === 'system' ? emailOn(c, settings, ctx) : true;
}

export const wantsDigest = (settings: NotificationSettings | null | undefined) => settings?.[DIGEST_KEY] === true;

/**
 * The effective technical contacts: the marked admins. While none is marked, every admin who hasn't turned system
 * alert emails off (the fallback); if all of them did, every admin, locked (`forced`), so an alert never goes to nobody.
 */
export function technicalContacts<T extends { id: string; role: Role; technicalContact: boolean; settings?: NotificationSettings | null }>(
  activeUsers: T[],
): { ids: Set<string>; fallback: boolean; forced: boolean } {
  const admins = activeUsers.filter(u => u.role === 'admin');
  const marked = admins.filter(u => u.technicalContact);
  if (marked.length) return { ids: new Set(marked.map(u => u.id)), fallback: false, forced: false };
  const willing = admins.filter(u => u.settings?.[emailKey('system')] !== false);
  if (willing.length) return { ids: new Set(willing.map(u => u.id)), fallback: true, forced: false };
  return { ids: new Set(admins.map(u => u.id)), fallback: true, forced: true };
}

/** Every notification off: each category in the app and by email (a locked system alert email stays on). */
export const ALL_OFF: NotificationSettings = Object.fromEntries(
  NOTIFY_CATEGORIES.flatMap(c => [
    [appKey(c), false],
    [emailKey(c), false],
  ]),
);

/**
 * Cleans a settings update: known keys, booleans only. `all: false` turns every notification off and `reset: true`
 * goes use the defaults; both replace the stored choices instead of merging into them.
 */
export function parseSettingsUpdate(body: unknown): { settings: NotificationSettings; replace: boolean } {
  const out: NotificationSettings = {};
  if (!body || typeof body !== 'object') return { settings: out, replace: false };
  const b = body as { digest?: unknown; email?: unknown; app?: unknown; all?: unknown; reset?: unknown };
  if (b.reset === true) return { settings: {}, replace: true };
  if (b.all === false) return { settings: { ...ALL_OFF }, replace: true };
  if (typeof b.digest === 'boolean') out[DIGEST_KEY] = b.digest;
  for (const [field, key] of [
    ['email', emailKey],
    ['app', appKey],
  ] as const) {
    const v = b[field];
    if (v && typeof v === 'object')
      for (const [k, on] of Object.entries(v as Record<string, unknown>))
        if (isNotifyCategory(k) && typeof on === 'boolean') out[key(k)] = on;
  }
  return { settings: out, replace: false };
}

export interface NotificationCategoryView {
  key: NotifyCategory;
  label: string;
  help: string;
  inApp: boolean;
  email: boolean;
  /** Set when the person can't turn the email off, with the reason. */
  locked: string | null;
}

export interface NotificationPrefsView {
  /** One email a day with everything held, instead of one email per event. */
  digest: boolean;
  technicalContact: boolean;
  /** No admin is marked as a technical contact, so every admin who hasn't turned them off gets system alerts. */
  fallback: boolean;
  /** Nothing reaches this person, in the app or by email (a locked system alert email aside). */
  allOff: boolean;
  /** No choice stored: every category follows the defaults. */
  defaults: boolean;
  categories: NotificationCategoryView[];
}

export function notificationPrefsView(
  settings: NotificationSettings | null | undefined,
  ctx: RecipientContext & { marked: boolean; fallback: boolean },
): NotificationPrefsView {
  const categories = NOTIFY_CATEGORIES.filter(c => categoryApplies(c, ctx.role)).map(c => ({
    key: c,
    ...NOTIFY_CATEGORY_LABELS[c],
    inApp: inAppOn(c, settings, ctx),
    email: emailOn(c, settings, ctx),
    locked: emailLocked(c, ctx)
      ? ctx.marked
        ? 'You are a technical contact, so system alerts always email you. An admin can change this in Users & access.'
        : 'No admin is a technical contact and every admin turned system alerts off, so they still email every admin. An admin can choose who gets them in Users & access.'
      : null,
  }));
  return {
    digest: wantsDigest(settings),
    technicalContact: ctx.marked,
    fallback: ctx.fallback,
    allOff: categories.every(c => !c.inApp && (!c.email || !!c.locked)),
    defaults: !settings || Object.keys(settings).length === 0,
    categories,
  };
}

/** Why this person gets this email: the footer of every notification email, with where to change it. */
export function emailFooter(c: NotifyCategory, ctx: { technicalContact: boolean; marked: boolean }): string {
  const why =
    c === 'system' && ctx.technicalContact
      ? ctx.marked
        ? 'you are a technical contact for system alerts'
        : 'no admin is a technical contact yet, so system alerts go to every admin who has not turned them off'
      : `your email choice for “${NOTIFY_CATEGORY_LABELS[c].label}” is on`;
  return `--\nYou get this email because ${why}. Choose which emails you get: your account menu (top right) → Notification settings.`;
}

/** The daily email of held notifications (counts and generic lines only; no content, as in every email). */
export function heldDigestText(items: { category: NotifyCategory; subject: string; createdAt: number }[], env: string): string {
  const lines = NOTIFY_CATEGORIES.flatMap(c => {
    const mine = items.filter(i => i.category === c);
    if (!mine.length) return [];
    return [
      `${NOTIFY_CATEGORY_LABELS[c].label} (${mine.length})`,
      ...mine.map(i => `- ${new Date(i.createdAt).toISOString().slice(11, 16)} UTC  ${i.subject.replace(/^Hoopoe: /, '')}`),
      '',
    ];
  });
  return [
    `Your Hoopoe notifications from the last 24 hours${env === 'production' ? '' : ` (${env})`}:`,
    '',
    ...lines,
    'Open Hoopoe to see them in your notifications.',
    '',
    'You get one email a day because you chose a daily summary. Change it: your account menu → Notification settings.',
  ].join('\n');
}
